import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { GAME_TYPES } from '@/features/gameSession/store/constants';
import { getInitialSnakePositions, getOppositeDirection } from '@/features/letterParty/helpers';
import { useGameControls } from '@/features/letterParty/hooks/useGameControls';
import { useLetterPartyDamage } from '@/features/letterParty/hooks/useLetterPartyDamage';
import { useQuestionSetup } from '@/features/letterParty/hooks/useQuestionSetup';
import { useWordAudio } from '@/features/letterParty/hooks/useWordAudio';
import useAudioPlayer from '@/shared/hooks/useAudioPlayer';
import { GameAudioProvider, useResolvedGameAudioOn } from '@/shared/hooks/useGameAudioSettings';
import useResponsive from '@/shared/hooks/useResponsive';
import { initialStreakState, streakReducer } from '@/features/letterParty/streakReducer';
import { LetterPartyThemeProvider, useLetterPartyTheme } from '@/features/letterParty/theme';
import { IS_STAGING_ENVIRONMENT } from '@/shared/config/env';
// used temporarily until layout is decided
import {
	GRID_PLACEMENT_ATTEMPT_LIMIT,
	getGridCell,
	getGridPositionColorSeed,
	getGridPositionKey,
	placeAvailableGridPosition,
	reserveGridPosition,
	reserveSnakeLeaderBuffer,
	reserveSnakePositions,
} from '@/features/letterParty/gridPlacement';
import {
	getMovedPosition,
	getPathDirectionAtDistance,
	getSnakePositionsFromPath,
	getTurnedPath,
	trimSnakePath,
} from '@/features/letterParty/snakePath';
import { EndOutcome } from '@/features/gameSession/types';
import { GameStatus } from '@/features/letterParty/types';
import { showFireworksConfetti } from '@/shared/utils/effects';
import { getElapsedSeconds } from '@/shared/utils/time';

import ControlBar from '@/features/letterParty/components/controlBar/ControlBar';
import EndScreen from '@/features/letterParty/components/endScreen/EndScreen';
import FlyingLetter from '@/features/letterParty/components/FlyingLetter';
import GameBoard, {
	GameBoardHandle,
	GRID_HEIGHT_LANDSCAPE,
	GRID_HEIGHT_PORTRAIT,
	GRID_WIDTH_LANDSCAPE,
	GRID_WIDTH_PORTRAIT,
} from '@/features/letterParty/components/gameBoard/GameBoard';
import KeyInstructions from '@/features/letterParty/components/KeyInstructions';
import MovementButtons from '@/features/letterParty/components/MovementButtons';
import StartScreen from '@/features/letterParty/components/startScreen/StartScreen';

import confettiSound from '@/features/letterParty/assets/sounds/confetti.ogg';
import falseAnswerSound from '@/features/letterParty/assets/sounds/false-answer.ogg';
import goodAnswerSound from '@/features/letterParty/assets/sounds/good-answer.ogg';
import musicSound from '@/features/letterParty/assets/sounds/music.ogg';
import partyHatSound from '@/features/letterParty/assets/sounds/party-hat.ogg';
import wallHitSound from '@/features/letterParty/assets/sounds/wall-hit.ogg';

import '@/features/letterParty/styles/letter-party-game.scss';

import type { WordProgressHandle } from '@/features/letterParty/components/controlBar/WordProgress';
import type { MovementButtonMode } from '@/features/letterParty/components/MovementButtons';
import type { Direction, Position } from '@/features/letterParty/types';
import type {
	GameAnswerEvent,
	GameAudioProps,
	GameCloseEvent,
	GameData,
	GameFinishEvent,
	Question,
} from '@/features/gameSession/types';

interface FlyingLetterAnimation {
	id: string;
	letter: string;
	letterIndex: number;
	from: { x: number; y: number };
	to: { x: number; y: number };
}

const DECAY_VIBE_INTERVAL_MS = 5000;
const GAME_LOOP_INTERVAL_MS = 545;
const MOVEMENT_BUTTON_MODE: MovementButtonMode = 'change-on-click'; // need to talk with JR team how we will know they are requesting the game

const SAFE_AREA_WIDTH = 5;
const SAFE_AREA_HEIGHT = 5;
const SEGMENTS_TO_REMOVE = 1;

function isAheadOfHead(head: Position, segment: Position, headDirection: Direction): boolean {
	switch (headDirection) {
		case 'UP':
			return segment.y < head.y;
		case 'DOWN':
			return segment.y > head.y;
		case 'LEFT':
			return segment.x < head.x;
		case 'RIGHT':
			return segment.x > head.x;
	}
}

function isSelfCollision(
	head: Position,
	headDirection: Direction,
	segment: Position,
	segmentDirection: Direction | null,
	nextGridCell: Position,
): boolean {
	if (!segmentDirection) {
		return false;
	}

	return (
		getGridPositionKey(segment) === getGridPositionKey(nextGridCell) &&
		segmentDirection !== getOppositeDirection(headDirection) &&
		isAheadOfHead(head, segment, headDirection)
	);
}

export type LetterPartyGameProps = {
	questions?: Question[];
	gameData?: GameData | null;
	themeKey?: string | null;
	levelProgress?: any;
	onAnswer?: (event: GameAnswerEvent) => void;
	onFinish?: (event: GameFinishEvent) => void;
	onClose: (event?: GameCloseEvent) => void;
	onReplay?: (gameData: GameData) => void;
} & GameAudioProps;

function LetterPartyGameContent({
	questions: providedQuestions,
	gameData: providedGameData,
	levelProgress,
	onAnswer,
	onFinish,
	onClose,
	onReplay,
}: LetterPartyGameProps) {
	const questions = providedQuestions ?? [];
	const gameData = providedGameData ?? null;
	const { isMobile } = useResponsive();
	const audioOn = useResolvedGameAudioOn();
	const theme = useLetterPartyTheme();

	const bestScore = gameData?.game?.high_score || 3;

	// Detect orientation
	const [isPortrait, setIsPortrait] = useState(() => window.innerHeight > window.innerWidth);

	// Set grid dimensions based on orientation
	const GRID_WIDTH = isPortrait ? GRID_WIDTH_PORTRAIT : GRID_WIDTH_LANDSCAPE;
	const GRID_HEIGHT = isPortrait ? GRID_HEIGHT_PORTRAIT : GRID_HEIGHT_LANDSCAPE;

	// Game state
	const [showStartScreen, setShowStartScreen] = useState(true);
	const [snake, setSnake] = useState<Position[]>(() => getInitialSnakePositions());
	const [collectedLetters, setCollectedLetters] = useState<string>('');
	const [revealedLetters, setRevealedLetters] = useState<string>('');
	const [gameStatus, setGameStatus] = useState<GameStatus>(GameStatus.Playing);
	const [vibeLevel, setVibeLevel] = useState(45); // Start at 45% (for music only)
	const [{ snakeSpeed }, updateStreakState] = useReducer(streakReducer, initialStreakState);
	const [completedWord, setCompletedWord] = useState<string | null>(null); // For showing completed word
	const [hatCount, setHatCount] = useState(0);
	const [isRecovering, setIsRecovering] = useState(false); // Pause after taking damage
	const [isStopped, setIsStopped] = useState(false); // Snake stopped, waiting for new direction
	const [waitingForFirstDirection, setWaitingForFirstDirection] = useState(false); // Waiting for player to choose direction
	const [flyingLetters, setFlyingLetters] = useState<FlyingLetterAnimation[]>([]);
	const [showKeyInstructions, setShowKeyInstructions] = useState(false); // Show key instructions after inactivity
	const boardHandleRef = useRef<GameBoardHandle | null>(null);
	const wordProgressRef = useRef<WordProgressHandle | null>(null);
	const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
	const hasAnsweredQuestionsRef = useRef(false);
	const hasFinishedRef = useRef(false);
	const questionStartedAtRef = useRef<number | null>(null);
	const movementBlockedRef = useRef(false);

	const directionRef = useRef<Direction>('RIGHT');
	const snakePathRef = useRef<Position[]>(snake);
	const lastGridCellRef = useRef<Position>(getGridCell(snake[0]));
	const hasInitializedRef = useRef(false);
	const snakeLength = snake.length;
	const SNAKE_SPEED_CELLS_PER_MS = snakeSpeed / GAME_LOOP_INTERVAL_MS;

	const setStreak = useCallback((correct: boolean) => {
		updateStreakState({ type: 'answer', correct });
	}, []);

	const emitFinish = useCallback(
		(gameOutcome: EndOutcome, score?: number) => {
			if (hasFinishedRef.current) {
				return;
			}

			hasFinishedRef.current = true;
			onFinish?.({
				gameOutcome,
				score,
			});
		},
		[onFinish],
	);

	// Callbacks for board setup hook
	const handleResetSnake = useCallback(() => {
		const initialSnake = getInitialSnakePositions();

		snakePathRef.current = initialSnake;
		lastGridCellRef.current = getGridCell(initialSnake[0]);
		directionRef.current = 'RIGHT';
		movementBlockedRef.current = false;

		setSnake(initialSnake);
		setCollectedLetters('');
		setRevealedLetters('');
		setFlyingLetters([]);
		setGameStatus(GameStatus.Playing);
		setWaitingForFirstDirection(true);
	}, []);

	const handleResetGameState = useCallback(() => {
		setVibeLevel(45);
		setIsStopped(false);
		setHatCount(0);
		updateStreakState({ type: 'reset' });
	}, []);

	const handleResetWordProgress = useCallback(() => {
		setCollectedLetters('');
		setRevealedLetters('');
		setFlyingLetters([]);
	}, []);

	const handlePauseSnake = useCallback(() => {
		movementBlockedRef.current = true;
		lastGridCellRef.current = getGridCell(snakePathRef.current[0]);
		setWaitingForFirstDirection(true);
	}, []);

	// Question setup hook - handles word/letter/hat/confetti management
	const {
		currentWord,
		currentQuestionIndex,
		letterQs,
		hats,
		confettis,
		setLetterQs,
		setHats,
		setConfettis,
		startNewWord,
		initGame,
	} = useQuestionSetup({
		questions,
		snake,
		gridWidth: GRID_WIDTH,
		gridHeight: GRID_HEIGHT,
		safeAreaWidth: SAFE_AREA_WIDTH,
		safeAreaHeight: SAFE_AREA_HEIGHT,
		onResetSnake: handleResetSnake,
		onResetGameState: handleResetGameState,
		onResetWordProgress: handleResetWordProgress,
		onPauseSnake: handlePauseSnake,
	});

	// Detect orientation changes
	useEffect(() => {
		const handleOrientationChange = () => {
			setIsPortrait(window.innerHeight > window.innerWidth);
		};
		window.addEventListener('resize', handleOrientationChange);
		return () => window.removeEventListener('resize', handleOrientationChange);
	}, []);

	useEffect(() => {
		setHatCount((prev) => Math.min(prev, snakeLength));
	}, [snakeLength]);

	const { handlePlayWord, isWordAudioPlaying } = useWordAudio({
		currentWord,
		shouldAutoPlay: !showStartScreen,
	});

	const { play: playBackgroundMusic, pause: pauseBackgroundMusic } = useAudioPlayer({
		src: musicSound,
		loop: true,
		autoplay: true,
		volume: 0.3,
	});

	const goodAnswerAudio = useAudioPlayer({ src: goodAnswerSound, volume: 0.5 });
	const falseAnswerAudio = useAudioPlayer({
		src: falseAnswerSound,
		volume: 0.5,
	});
	const partyHatAudio = useAudioPlayer({ src: partyHatSound, volume: 0.5 });
	const wallHitAudio = useAudioPlayer({ src: wallHitSound, volume: 1 });
	const confettiAudio = useAudioPlayer({ src: confettiSound, volume: 0.5 });

	const { applyDamage } = useLetterPartyDamage({
		setGameStatus,
		setIsRecovering,
		setIsStopped,
		setVibeLevel,
	});

	useEffect(() => {
		if (!audioOn || isWordAudioPlaying) {
			pauseBackgroundMusic();
		} else {
			playBackgroundMusic();
		}
	}, [audioOn, isWordAudioPlaying, playBackgroundMusic, pauseBackgroundMusic]);

	const triggerLetterFlyAnimation = useCallback((letter: string, letterIndex: number, gridPosition: Position) => {
		const targetRect = wordProgressRef.current?.getLetterBoxRect(letterIndex);
		const boardPosition = boardHandleRef.current?.getCellCenterPosition(gridPosition);

		if (!targetRect || !boardPosition) {
			return;
		}

		const { x: startX, y: startY } = boardPosition;
		const endX = targetRect.left + targetRect.width / 2;
		const endY = targetRect.top + targetRect.height / 2;
		const lowercaseLetter = letter.toLowerCase();

		setFlyingLetters((prev) => [
			...prev,
			{
				id: `${lowercaseLetter}-${letterIndex}-${Date.now()}`,
				letter: lowercaseLetter,
				letterIndex,
				from: { x: startX, y: startY },
				to: { x: endX, y: endY },
			},
		]);
	}, []);

	const handleRotateEntities = useCallback(
		(rotatePosition: (pos: Position) => Position) => {
			setSnake((prevSnake) => {
				const rotatedSnake = prevSnake.map(rotatePosition);
				snakePathRef.current = snakePathRef.current.map(rotatePosition);
				lastGridCellRef.current = getGridCell(snakePathRef.current[0]);
				return rotatedSnake;
			});

			setLetterQs((prevLetters) =>
				prevLetters.map((letter) => {
					const position = rotatePosition(letter.position);
					return {
						...letter,
						position,
						colorSeed: position.x * 100 + position.y,
					};
				}),
			);

			setHats((prevHats) =>
				prevHats.map((hat) => ({
					...hat,
					position: rotatePosition(hat.position),
				})),
			);

			setConfettis((prevConfettis) =>
				prevConfettis.map((confetti) => ({
					...confetti,
					position: rotatePosition(confetti.position),
				})),
			);
		},
		[setLetterQs, setHats, setConfettis],
	);

	const handleFlyingLetterComplete = useCallback((id: string, letter: string, letterIndex: number) => {
		setFlyingLetters((prev) => prev.filter((animation) => animation.id !== id));
		setRevealedLetters((prev) => {
			if (prev.length > letterIndex) {
				return prev;
			}

			if (prev.length === letterIndex) {
				return `${prev}${letter.toLowerCase()}`;
			}

			return prev;
		});
	}, []);

	const advanceSnakeStateRef = useRef({
		gridWidth: GRID_WIDTH,
		gridHeight: GRID_HEIGHT,
		hats,
		confettis,
		letterQs,
		collectedLetters,
		currentWord,
		applyDamage,
		triggerLetterFlyAnimation,
		playCurrentWordAudio: handlePlayWord,
		playConfetti: confettiAudio.play,
		playFalseAnswer: falseAnswerAudio.play,
		playGoodAnswer: goodAnswerAudio.play,
		playPartyHat: partyHatAudio.play,
		playWallHit: wallHitAudio.play,
	});

	useEffect(() => {
		advanceSnakeStateRef.current = {
			gridWidth: GRID_WIDTH,
			gridHeight: GRID_HEIGHT,
			hats,
			confettis,
			letterQs,
			collectedLetters,
			currentWord,
			applyDamage,
			triggerLetterFlyAnimation,
			playCurrentWordAudio: handlePlayWord,
			playConfetti: confettiAudio.play,
			playFalseAnswer: falseAnswerAudio.play,
			playGoodAnswer: goodAnswerAudio.play,
			playPartyHat: partyHatAudio.play,
			playWallHit: wallHitAudio.play,
		};
	}, [
		GRID_HEIGHT,
		GRID_WIDTH,
		applyDamage,
		collectedLetters,
		confettiAudio.play,
		confettis,
		currentWord,
		falseAnswerAudio.play,
		goodAnswerAudio.play,
		handlePlayWord,
		hats,
		letterQs,
		partyHatAudio.play,
		triggerLetterFlyAnimation,
		wallHitAudio.play,
	]);

	const advanceSnake = useCallback(
		(distance: number) => {
			setSnake((prevSnake) => {
				const {
					gridWidth,
					gridHeight,
					hats: currentHats,
					confettis: currentConfettis,
					letterQs: currentLetterQs,
					collectedLetters: currentCollectedLetters,
					currentWord: activeWord,
					applyDamage: applyCurrentDamage,
					triggerLetterFlyAnimation: triggerCurrentLetterFlyAnimation,
					playCurrentWordAudio,
					playConfetti,
					playFalseAnswer,
					playGoodAnswer,
					playPartyHat,
					playWallHit,
				} = advanceSnakeStateRef.current;
				const currentPath = snakePathRef.current;
				const currentHead = currentPath[0];

				if (!currentHead) {
					return prevSnake;
				}

				const applyDamageToPath = (damagedSnake: Position[], playHitSound: () => void) => {
					movementBlockedRef.current = true;
					const nextSnake = applyCurrentDamage({
						currentSnake: prevSnake,
						damagedSnake,
						playHitSound,
					});

					snakePathRef.current = trimSnakePath(currentPath, nextSnake.length);
					lastGridCellRef.current = getGridCell(snakePathRef.current[0]);

					return nextSnake;
				};

				const newHead = getMovedPosition(currentHead, directionRef.current, distance);
				const nextGridCell = getGridCell(newHead);
				const currentGridCell = lastGridCellRef.current;
				const hasEnteredNewGridCell =
					nextGridCell.x !== currentGridCell.x || nextGridCell.y !== currentGridCell.y;

				if (
					hasEnteredNewGridCell &&
					(nextGridCell.x < 0 ||
						nextGridCell.x >= gridWidth ||
						nextGridCell.y < 0 ||
						nextGridCell.y >= gridHeight)
				) {
					const newSnakeAfterDamage = prevSnake.slice(0, prevSnake.length - SEGMENTS_TO_REMOVE);

					if (newSnakeAfterDamage.length > 1) {
						playCurrentWordAudio(activeWord, true);
					}

					return applyDamageToPath(newSnakeAfterDamage, playWallHit);
				}

				const movedPath = [newHead, ...currentPath.slice(1)];

				if (hasEnteredNewGridCell) {
					const movedSnake = getSnakePositionsFromPath(movedPath, prevSnake.length);
					const hitSegmentIndex = movedSnake.findIndex((segment, index) => {
						if (index === 0) {
							return false;
						}
						const segmentDirection = getPathDirectionAtDistance(movedPath, index);
						return isSelfCollision(newHead, directionRef.current, segment, segmentDirection, nextGridCell);
					});

					if (hitSegmentIndex !== -1) {
						const newSnakeAfterDamage = getSnakePositionsFromPath(currentPath, hitSegmentIndex);

						return applyDamageToPath(newSnakeAfterDamage, playWallHit);
					}
				}

				// Check hat collection (leader pickup)
				const hatAtPosition = hasEnteredNewGridCell
					? currentHats.find(
							(hat) =>
								!hat.collected &&
								hat.position.x === nextGridCell.x &&
								hat.position.y === nextGridCell.y,
					  )
					: undefined;

				if (hatAtPosition && prevSnake[0]) {
					setHatCount((prev) => Math.min(prevSnake.length, prev + 1));
					setHats((prevHats) =>
						prevHats.map((hat) => (hat.id === hatAtPosition.id ? { ...hat, collected: true } : hat)),
					);

					setVibeLevel((prev) => Math.min(100, prev + 5));
					playPartyHat();
				}

				// Check confetti collection (leader pickup)
				const confettiAtPosition = hasEnteredNewGridCell
					? currentConfettis.find(
							(confetti) =>
								!confetti.collected &&
								confetti.position.x === nextGridCell.x &&
								confetti.position.y === nextGridCell.y,
					  )
					: undefined;

				if (confettiAtPosition && prevSnake[0]) {
					// Mark confetti as collected
					setConfettis((prevConfettis) =>
						prevConfettis.map((confetti) =>
							confetti.id === confettiAtPosition.id ? { ...confetti, collected: true } : confetti,
						),
					);

					// Show confetti effect for 2 seconds
					showFireworksConfetti('game-confetti', 2000);
					playConfetti();

					// Increase vibe level significantly
					setVibeLevel((prev) => Math.min(100, prev + 20));
				}

				// Check letter Q collection
				const nextLetterIndex = currentCollectedLetters.length;
				const nextLetterNeeded = activeWord.toLowerCase()[nextLetterIndex];

				// Find if there's a letter at the new head position
				const letterAtPosition = hasEnteredNewGridCell
					? currentLetterQs.find(
							(letterQ) =>
								!letterQ.collected &&
								letterQ.position.x === nextGridCell.x &&
								letterQ.position.y === nextGridCell.y,
					  )
					: undefined;

				let snakeLengthAfterMove = prevSnake.length;

				if (letterAtPosition) {
					if (letterAtPosition.letter.toLowerCase() === nextLetterNeeded) {
						// Correct letter collected - add 1 Q to polonaise!
						triggerCurrentLetterFlyAnimation(
							letterAtPosition.letter,
							nextLetterIndex,
							letterAtPosition.position,
						);
						snakeLengthAfterMove += 1;
						setCollectedLetters((prev) => prev + letterAtPosition.letter);

						setStreak(true);

						// Increase vibe level on correct letter
						setVibeLevel((prev) => Math.min(100, prev + 15));

						// Play party sound for correct letter!
						playGoodAnswer();

						// Mark letter as collected
						setLetterQs((prevLetterQs) =>
							prevLetterQs.map((letterQ) =>
								letterQ === letterAtPosition ? { ...letterQ, collected: true } : letterQ,
							),
						);
					} else {
						const newSnakeAfterDamage = prevSnake.slice(0, prevSnake.length - SEGMENTS_TO_REMOVE);

						if (newSnakeAfterDamage.length > 1) {
							playCurrentWordAudio(activeWord, true);
						}

						setStreak(false);

						// Check if this letter is needed in the word (but just wrong timing)
						const isNeededLetter = activeWord.toLowerCase().includes(letterAtPosition.letter.toLowerCase());

						if (isNeededLetter) {
							// This letter is needed! Respawn it somewhere else
							setLetterQs((prevLetterQs) => {
								const usedPositions = new Set<string>();

								// Mark all current positions as used
								prevLetterQs.forEach((lq) => {
									if (!lq.collected && lq !== letterAtPosition) {
										reserveGridPosition(usedPositions, lq.position);
									}
								});
								reserveSnakePositions(usedPositions, prevSnake);
								reserveSnakeLeaderBuffer(usedPositions, prevSnake);
								currentHats.forEach((hat) => {
									if (!hat.collected) {
										reserveGridPosition(usedPositions, hat.position);
									}
								});

								const position = placeAvailableGridPosition({
									gridWidth,
									gridHeight,
									usedPositions,
									maxAttempts: GRID_PLACEMENT_ATTEMPT_LIMIT,
								});

								if (!position) {
									return prevLetterQs;
								}

								// Update the letter position
								return prevLetterQs.map((letterQ) =>
									letterQ === letterAtPosition
										? { ...letterQ, position, colorSeed: getGridPositionColorSeed(position) }
										: letterQ,
								);
							});
						} else {
							// This letter is not needed - remove it permanently
							setLetterQs((prevLetterQs) =>
								prevLetterQs.map((letterQ) =>
									letterQ === letterAtPosition ? { ...letterQ, collected: true } : letterQ,
								),
							);
						}

						return applyDamageToPath(newSnakeAfterDamage, playFalseAnswer);
					}
				}

				const nextPath = trimSnakePath(movedPath, snakeLengthAfterMove);
				const nextSnake = getSnakePositionsFromPath(nextPath, snakeLengthAfterMove);

				snakePathRef.current = nextPath;
				lastGridCellRef.current = nextGridCell;

				return nextSnake;
			});
		},
		[setConfettis, setHats, setLetterQs, setStreak],
	);
	const turnSnake = useCallback((newDirection: Direction, previousDirection: Direction) => {
		if (newDirection !== previousDirection) {
			snakePathRef.current = getTurnedPath(snakePathRef.current);
		}

		directionRef.current = newDirection;
	}, []);

	const handleFirstDirectionChosen = useCallback(() => {
		movementBlockedRef.current = false;
		setWaitingForFirstDirection(false);
		setShowKeyInstructions(false);
	}, []);

	const handleResume = useCallback(() => {
		movementBlockedRef.current = false;
		setIsStopped(false);
		setShowKeyInstructions(false);
	}, []);

	const handleMovementStop = useCallback(() => {
		if (gameStatus !== GameStatus.Playing || waitingForFirstDirection || isRecovering || completedWord !== null) {
			return;
		}

		movementBlockedRef.current = true;
		lastGridCellRef.current = getGridCell(snakePathRef.current[0]);
		setIsStopped(true);
	}, [completedWord, gameStatus, isRecovering, waitingForFirstDirection]);

	// Game controls hook - handles keyboard input and validates non-keyboard direction changes
	const handleDirectionChange = useGameControls({
		gameStatus,
		waitingForFirstDirection,
		isRecovering,
		isStopped,
		directionRef,
		onFirstDirectionChosen: handleFirstDirectionChosen,
		onResume: handleResume,
		onDirectionChange: turnSnake,
	});

	const handleSwipeDirection = useCallback(
		(direction: Direction) => {
			if (MOVEMENT_BUTTON_MODE === 'hold-to-move') {
				return;
			}

			handleDirectionChange(direction);
		},
		[handleDirectionChange],
	);

	const startGame = useCallback(() => {
		initGame();
	}, [initGame]);

	const handleStartGame = useCallback(() => {
		setShowStartScreen(false);
	}, []);

	const handleClose = useCallback(
		(finishEvent?: GameFinishEvent) => {
			onClose(finishEvent ? { finishEvent } : undefined);
		},
		[onClose],
	);

	const handleBack = useCallback(() => {
		handleClose(hasAnsweredQuestionsRef.current ? { gameOutcome: EndOutcome.ABANDONED } : undefined);
	}, [handleClose]);

	const handleReplay = useCallback(() => {
		const nextGameData = levelProgress?.user_learning_path_section;

		if (nextGameData) {
			onReplay?.(nextGameData);
		}
	}, [levelProgress, onReplay]);

	useEffect(() => {
		// Only finish the game here for GameOver (LOST).
		// Completed (SUCCEEDED) is handled when the last question resolves.
		if (gameStatus === GameStatus.GameOver && hasAnsweredQuestionsRef.current) {
			emitFinish(EndOutcome.LOST);
		}
	}, [emitFinish, gameStatus]);

	useEffect(() => {
		if (hasInitializedRef.current) return;
		if (!questions || questions.length === 0) return;

		hasInitializedRef.current = true;
		startGame();
	}, [startGame, questions]);

	useEffect(() => {
		if (showStartScreen || !currentWord || waitingForFirstDirection) {
			questionStartedAtRef.current = null;
			return;
		}

		if (questionStartedAtRef.current == null) {
			questionStartedAtRef.current = Date.now();
		}
	}, [currentQuestionIndex, currentWord, showStartScreen, waitingForFirstDirection]);

	// Game loop
	useEffect(() => {
		if (gameStatus !== GameStatus.Playing) return;
		if (waitingForFirstDirection) return; // Don't start until player chooses direction
		if (completedWord !== null) return; // Pause during word completion celebration
		if (isRecovering) return; // Pause during recovery after taking damage
		if (isStopped) return; // Pause when stopped, waiting for player input

		movementBlockedRef.current = false;

		let animationFrameId: number;
		let previousTimestamp: number | null = null;

		const moveSnake = (timestamp: number) => {
			if (previousTimestamp !== null) {
				const deltaCells = (timestamp - previousTimestamp) * SNAKE_SPEED_CELLS_PER_MS;
				advanceSnake(Math.min(deltaCells, 1)); //in case of gaps/hitches, always advance at the most 1 cell
			}

			if (movementBlockedRef.current) {
				return;
			}

			previousTimestamp = timestamp;
			animationFrameId = requestAnimationFrame(moveSnake);
		};

		animationFrameId = requestAnimationFrame(moveSnake);

		return () => cancelAnimationFrame(animationFrameId);
	}, [
		gameStatus,
		completedWord,
		isRecovering,
		isStopped,
		waitingForFirstDirection,
		advanceSnake,
		SNAKE_SPEED_CELLS_PER_MS,
	]);

	// Check win condition - show word completion celebration
	useEffect(() => {
		if (completedWord !== null) {
			return;
		}

		if (revealedLetters.toLowerCase() === currentWord.toLowerCase() && currentWord.length > 0) {
			const currentQuestion = questions[currentQuestionIndex];
			const answerTimeInSeconds = getElapsedSeconds(questionStartedAtRef.current);

			if (currentQuestion) {
				const isLastQuestion = currentQuestionIndex >= questions.length - 1;
				const finishEvent = isLastQuestion
					? {
							gameOutcome: EndOutcome.SUCCEEDED,
							score: snakeLength,
					  }
					: undefined;

				onAnswer?.({
					gameType: GAME_TYPES.LETTER_PARTY_GAME,
					question: currentQuestion,
					questionIndex: currentQuestionIndex,
					isCorrect: true,
					answerTexts: [currentWord],
					score: snakeLength,
					answerTimeInSeconds,
					data: {
						completedWord: currentWord,
						revealedLetters,
					},
					finishEvent,
				});

				hasAnsweredQuestionsRef.current = true;
			}

			// Word completed! Show celebration and speak the word
			setCompletedWord(currentWord);
			setVibeLevel((prev) => Math.min(100, prev + 10));
			showFireworksConfetti('game-confetti', 2000);

			// Play confetti sound (word audio already played automatically via useWordAudio hook)
			confettiAudio.play();

			// Check if this was the last question (game complete)
			if (currentQuestionIndex >= questions.length - 1) {
				// Game completed! All questions done
				setTimeout(() => {
					setGameStatus(GameStatus.Completed);
				}, 2000);
			} else {
				// After 2 seconds, start next word
				setTimeout(() => {
					setCompletedWord(null);
					setGameStatus(GameStatus.Playing); // Resume playing
					startNewWord(true, true);
				}, 2000);
			}
		}
	}, [
		revealedLetters,
		currentWord,
		completedWord,
		currentQuestionIndex,
		startNewWord,
		questions,
		onAnswer,
		snakeLength,
	]);

	// Vibe decay during gameplay (slow decline if no action) - affects music only, no life loss
	useEffect(() => {
		if (gameStatus !== GameStatus.Playing) return;
		if (waitingForFirstDirection) return; // Don't decay while waiting for first direction
		if (completedWord !== null) return; // Don't decay during celebration

		const decayInterval = setInterval(() => {
			setVibeLevel((prev) => Math.max(0, prev - 2));
		}, DECAY_VIBE_INTERVAL_MS);

		return () => clearInterval(decayInterval);
	}, [gameStatus, waitingForFirstDirection, completedWord]);

	// Show key instructions (immediately on game start, after 3 seconds when stopped)
	useEffect(() => {
		// Clear any existing timer
		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
			inactivityTimerRef.current = null;
		}

		// Only show instructions during gameplay when snake is paused
		if (gameStatus !== GameStatus.Playing) {
			setShowKeyInstructions(false);
			return;
		}

		// Show instructions immediately when waiting for first direction (game start)
		if (waitingForFirstDirection) {
			setShowKeyInstructions(true);
		} else if (isStopped) {
			// Show instructions after 3 seconds when snake stops after taking damage
			inactivityTimerRef.current = setTimeout(() => {
				setShowKeyInstructions(true);
			}, 10000);
		} else {
			// Hide instructions when snake is moving
			setShowKeyInstructions(false);
		}

		return () => {
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
				inactivityTimerRef.current = null;
			}
		};
	}, [gameStatus, waitingForFirstDirection, isStopped]);

	const isWordComplete = completedWord !== null;
	const questionsRemaining = Math.max(0, questions.length - currentQuestionIndex);

	const starProgressData = useMemo(() => {
		if (!levelProgress) {
			return null;
		}

		const {
			level_completed,
			level_stars_earned,
			stars_earned: progress_stars_earned,
			current_level,
		} = levelProgress;

		const totalStars = level_completed ? level_completed?.required_stars : current_level?.required_stars;

		const levelCompletedRequiredStars = level_completed?.required_stars;
		const rawEarned = level_stars_earned;
		const earnedStars =
			levelCompletedRequiredStars && rawEarned > levelCompletedRequiredStars
				? levelCompletedRequiredStars
				: rawEarned;

		const previousEarned = level_stars_earned - progress_stars_earned;

		return {
			progressStarsEarned: progress_stars_earned,
			totalStars,
			earnedStars: earnedStars,
			previousEarned: Math.max(0, previousEarned),
		};
	}, [levelProgress]);

	if (showStartScreen) {
		return <StartScreen onStart={handleStartGame} onBack={handleBack} bestScore={bestScore} />;
	}

	if (gameStatus === GameStatus.Completed || gameStatus === GameStatus.GameOver) {
		const currentLevel = levelProgress?.current_level;

		return (
			<EndScreen
				currentScore={snakeLength}
				bestScore={bestScore}
				showMilepost={Boolean(levelProgress)}
				progressStarsEarned={starProgressData?.progressStarsEarned}
				totalStars={starProgressData?.totalStars}
				earnedStars={starProgressData?.earnedStars}
				previousEarned={starProgressData?.previousEarned}
				levelImage={currentLevel?.level_image}
				levelName={currentLevel?.level_name}
				levelTitle={currentLevel?.level_title}
				levelCompleted={levelProgress?.level_completed}
				onClose={() => handleClose()}
				onReplay={handleReplay}
			/>
		);
	}

	return (
		<div className={`letter-party-game letter-party-game--${theme.key}`}>
			<div className="letter-party-game__screen">
				<ControlBar
					onBack={handleBack}
					currentWord={currentWord}
					revealedLetters={revealedLetters}
					onSpeak={handlePlayWord}
					wordProgressRef={wordProgressRef}
					isWordComplete={isWordComplete}
					questionsRemaining={questionsRemaining}
					snakeLength={snakeLength}
					isWordAudioPlaying={isWordAudioPlaying}
				/>

				<GameBoard
					ref={boardHandleRef}
					gridWidth={GRID_WIDTH}
					gridHeight={GRID_HEIGHT}
					isPortrait={isPortrait}
					isMobile={isMobile}
					canRotateEntities={hasInitializedRef.current}
					snake={snake}
					currentDirection={directionRef.current}
					hatCount={hatCount}
					hats={hats}
					confettis={confettis}
					letterQs={letterQs}
					isRecovering={isRecovering}
					waitingForFirstDirection={waitingForFirstDirection}
					isStopped={isStopped}
					onRotateEntities={handleRotateEntities}
					onSwipeDirection={handleSwipeDirection}
				/>

				{IS_STAGING_ENVIRONMENT && (
					<MovementButtons
						mode={MOVEMENT_BUTTON_MODE}
						onDirectionChange={handleDirectionChange}
						onMovementStop={handleMovementStop}
					/>
				)}

				{flyingLetters.map((animation) => (
					<FlyingLetter
						key={animation.id}
						id={animation.id}
						letter={animation.letter}
						letterIndex={animation.letterIndex}
						from={animation.from}
						to={animation.to}
						onComplete={handleFlyingLetterComplete}
					/>
				))}

				<KeyInstructions show={showKeyInstructions} isJunior={MOVEMENT_BUTTON_MODE === 'hold-to-move'} />
			</div>

			<canvas id="game-confetti" className="letter-party-game__confetti-screen" />
		</div>
	);
}

function LetterPartyGame(props: LetterPartyGameProps) {
	const { audioOn, onAudioChange, themeKey: providedThemeKey, ...restProps } = props;
	const themeKey = providedThemeKey ?? restProps.gameData?.free_play_theme;

	return (
		<GameAudioProvider audioOn={audioOn} onAudioChange={onAudioChange}>
			<LetterPartyThemeProvider themeKey={themeKey}>
				<LetterPartyGameContent {...restProps} />
			</LetterPartyThemeProvider>
		</GameAudioProvider>
	);
}

export default LetterPartyGame;
